import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { captureAttribution } from './utils/attribution';
import { captureReferral } from './utils/referral';
import { loadLocale } from './i18n';

// Antes de pintar nada: la URL de aterrizaje es la única que trae los `utm_*`.
captureAttribution();
// «Invita y gana»: guarda el código del enlace de un amigo (?ref=) para el registro.
captureReferral();

// El diccionario del idioma de la URL empieza a bajar ya, en paralelo con el resto del JS.
loadLocale((window.location.pathname.match(/^\/(en|es|it|pt|de|fr)(\/|$)/) || [])[1] || 'en');

const app = (
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

const prerendered = document.getElementById('root');
const hasPrerender = prerendered && prerendered.children.length > 0;
// Lo leen los componentes con animación de entrada (Hero): si el contenido ya
// viene pintado desde el HTML, repetir el fundido desde opacidad 0 hace que
// desaparezca y vuelva a entrar, y el navegador cuenta ese segundo pintado como
// LCP (medido: subtítulo del hero de 3,06 s a 6,04 s).
window.__PRERENDERED_BOOT = !!hasPrerender;

if (!hasPrerender) {
  // Sin HTML prerenderizado (desarrollo local o el propio prerender): arranque normal.
  ReactDOM.createRoot(prerendered).render(app);
} else {
  // Con HTML prerenderizado NO se monta React encima.
  //
  // Antes se hacía `createRoot(#root).render()`, que descarta el HTML
  // prerenderizado. Como las rutas son `lazy` con un `Suspense` vacío, la página
  // se veía, React la borraba, quedaba en blanco hasta que llegaba el trozo de
  // JS de la ruta, y reaparecía de golpe. Medido el 16-9-2026 en
  // /en/delivery-note-to-excel (móvil, CPU ×4): CLS 0,89 y LCP 9,98 s, con el
  // 99 % del LCP en «render delay» sobre un texto que YA venía en el HTML.
  //
  // `hydrateRoot` no es la salida: el prerender guarda el DOM tal cual lo deja
  // el navegador, sin las marcas <!--$--> que React emite en SSR para cada
  // Suspense, así que la hidratación fallaría y volvería a pintar desde cero.
  //
  // En su lugar React monta en un contenedor invisible y, cuando la ruta ya tiene
  // contenido, se intercambia por el prerenderizado. Son el mismo HTML, así que
  // el cambio no se ve ni desplaza nada.
  const mount = document.createElement('div');
  mount.style.cssText =
    'position:absolute;top:0;left:0;width:100%;visibility:hidden;pointer-events:none;';
  mount.setAttribute('aria-hidden', 'true');
  document.body.appendChild(mount);

  let swapped = false;
  const swap = () => {
    if (swapped) return;
    swapped = true;
    observer.disconnect();
    clearTimeout(fallback);
    prerendered.replaceWith(mount);
    mount.removeAttribute('style');
    mount.removeAttribute('aria-hidden');
    mount.id = 'root';
  };

  // Lista cuando el <main> tiene contenido (la ruta lazy ya resolvió).
  const ready = () => {
    const main = mount.querySelector('main');
    // `data-pending` marca contenido que aún llega en otro chunk (el cuerpo de
    // un artículo): intercambiar antes enseñaría el esqueleto en lugar del
    // texto prerenderizado.
    return !!main && main.children.length > 0 && !mount.querySelector('[data-pending]');
  };

  const observer = new MutationObserver(() => {
    if (ready()) swap();
  });
  observer.observe(mount, { childList: true, subtree: true });

  // Red de seguridad: si algo impide detectar el contenido, se intercambia igual.
  const fallback = setTimeout(swap, 10000);

  ReactDOM.createRoot(mount).render(app);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
